import h from 'hyperscript';
import { unByKey } from 'ol/Observable';

import { ProgressSpinner } from 'ui/progress-spinner/progress-spinner';

import { confirm } from 'cadenza/alert';
import { MapProperty } from 'cadenza/map/map-property';
import { SketchLayerProperty } from 'cadenza/map/classic-sketch-layer/sketch-layer-property';
import { getSketchState, updateLastSaveDate } from 'cadenza/map/classic-sketch-layer/sketch-state';
import { formatTime } from 'cadenza/utils/format/format';

import './sketch-controls.css';
import i18n from './sketch-controls.properties';

export class SketchControls extends HTMLElement {

  #layer;
  #map;
  #status;
  #eventKeys;

  constructor (layer, map) {
    super();
    this.#layer = layer;
    this.#map = map;
  }

  connectedCallback () {
    if (!this.classList.contains('d-legend-sketch-controls')) {
      this.classList.add('d-legend-sketch-controls');
      this.append(this.#status = h('p.status.d-alert.d-alert-small'));
    }

    this._update();
    this.#eventKeys = [
      this.#layer.on([
        `change:${SketchLayerProperty.OPACITY}`,
        `change:${SketchLayerProperty.LAST_SAVED_DATE}` ], () => this._update()),
      this.#map.on(`change:${MapProperty.SELECTED_LAYER}`, () => this._update()),
      this.#map.on(`change:${MapProperty.SKETCH_LAYER}`, () => this._update()),
      this.#layer.on(`change:${SketchLayerProperty.IS_SAVING}`, () => {
        updateLastSaveDate(this.#layer);
        this._update();
      }),
      this.#layer.on('preremove', cancellablePromiseEvent => cancellablePromiseEvent.preventDefault(this._preventRemove()))
    ];
  }

  disconnectedCallback () {
    unByKey(this.#eventKeys);
  }

  _preventRemove () {
    const { isSaved } = getSketchState(this.#map, this.#layer);
    if (!isSaved) {
      return confirm(i18n('confirm.remove.title'), i18n('confirm.remove.message')).then(confirmed => !confirmed); // prevent removing if not confirmed
    }
    return Promise.resolve(false); // remove the layer
  }

  _update () {
    const {
      isEditing,
      isSaving,
      isSaved,
      isSavedInDb,
      canSaveAsNewItemToDb,
      lastSavedDate,
      sharedWithEditPrivilegeButNotAllowedToEditInDBRepo
    } = getSketchState(this.#map, this.#layer);

    const isSelected = this.#map.get(MapProperty.SELECTED_LAYER) === this.#layer;

    const status = this.#status;
    status.classList.remove('d-alert-info');
    status.classList.remove('d-alert-warning');
    status.innerHTML = '';

    if (isSavedInDb) {
      if (isSelected && sharedWithEditPrivilegeButNotAllowedToEditInDBRepo) {
        status.classList.add('d-alert-info');
        status.append(i18n('status.not-editable-due-to-missing-edit-privilege-on-repository'));
      } else if (isSaving) {
        const progressSpinner = new ProgressSpinner({ label: i18n('status.saving'), size: 'xs' });
        progressSpinner.classList.add('d-progress-spinner-gray');
        status.append(progressSpinner);
      } else if (isSelected && isEditing) {
        status.classList.add('d-alert-info');
        status.append(lastSavedDate
          ? i18n('status.saved-with-time', { time: formatTime(lastSavedDate, { seconds: false }) })
          : i18n('status.saved'));
      }
    } else if (isEditing) {
      status.classList.add('d-alert-info');
      status.append((canSaveAsNewItemToDb && !isSaved) ? i18n('status.not-saved') : i18n('status.not-saveable'));
    }
  }

}

customElements.define('d-legend-sketch-controls', SketchControls);
